"use client";

import { Fragment, useEffect, useRef, useState } from "react";
import Image from "next/image";
import { ChevronDown, Menu, User } from "lucide-react";
import { Link } from "@/i18n/routing";
import {
  hotTopics,
  primaryNav,
  ticker,
  type Ticker,
} from "@/fixtures/shared/nav/nav-v2-fixtures";
import { MobileMegaMenu } from "./mobile-mega-menu";
import { MoreMenuPanel } from "./more-menu-panel";
import { StickyNav } from "./sticky-nav";
import { AdSlot } from "@/components/ads/ad-slot";
import {
  LanguageAction,
  NAV_ICON_BUTTON,
  SearchAction,
} from "./nav-actions";
import { AccountDrawer } from "@/components/account/shared/account-drawer";
import { CORE_COLORS } from "@/lib/theme/core";
import { ScrollEdgeArrows, useScrollEdges } from "@/components/shared/main/scroll-edges";
import { tagRoute } from "@/lib/routes";

const SUBSCRIBER_TEXTLINK_SIZES = { desktop: ["fluid", "220x30", "300x30"], tablet: [] as string[], mobile: [] as string[] };

export function Nav() {
  const [moreOpen, setMoreOpen] = useState(false);
  const [accountOpen, setAccountOpen] = useState(false);
  const [sticky, setSticky] = useState(false);
  const mainRef = useRef<HTMLDivElement>(null);
  const toggle = () => setMoreOpen((v) => !v);

  useEffect(() => {
    const el = mainRef.current;
    if (!el) return;
    const io = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        setSticky(!entry.isIntersecting);
      },
      { threshold: 0, rootMargin: "0px" },
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);

  return (
    <>
      <div ref={mainRef} className="w-full bg-white">
        <TickerBar />
        <div className="relative">
          <LogoRow
            moreOpen={moreOpen}
            onToggleMore={toggle}
            accountOpen={accountOpen}
            onOpenAccount={() => setAccountOpen(true)}
          />
          <PrimaryNav moreOpen={moreOpen} onToggleMore={toggle} />
          <MoreMenuPanel
            open={moreOpen}
            pinned={sticky}
            onClose={() => setMoreOpen(false)}
          />
        </div>
        <SubNav />
      </div>
      <StickyNav
        visible={sticky}
        moreOpen={moreOpen}
        onToggleMore={toggle}
        accountOpen={accountOpen}
        onOpenAccount={() => setAccountOpen(true)}
      />
      <MobileMegaMenu open={moreOpen} onClose={() => setMoreOpen(false)} />
      <AccountDrawer open={accountOpen} onOpenChange={setAccountOpen} />
    </>
  );
}

function TickerBar() {
  return (
    <div className="w-full bg-black">
      <div
        className="mx-auto flex w-full max-w-[1440px] items-stretch px-4 py-4 lg:px-[120px]"
        style={{ gap: 15 }}
      >
        <div
          className="flex min-w-0 flex-1 items-stretch overflow-x-auto no-scrollbar"
          style={{ gap: 15 }}
        >
          {ticker.map((t, i) => (
            <Fragment key={t.name}>
              {/* Mobile only renders the first 2 tickers; remainder appear on desktop. */}
              {i < 2 ? (
                <TickerCell row={t} />
              ) : (
                <div className="contents max-md:hidden">
                  <TickerCell row={t} />
                </div>
              )}
              {i < ticker.length - 1 && (
                <span
                  aria-hidden
                  className={`w-px shrink-0 self-stretch ${
                    i >= 1 ? "max-md:hidden" : ""
                  }`}
                  style={{ backgroundColor: "#6e6e73" }}
                />
              )}
            </Fragment>
          ))}
        </div>
      </div>
    </div>
  );
}

function TickerCell({ row }: { row: Ticker }) {
  const color = row.positive ? "#18ac79" : "#eb555e";
  const arrow = row.positive ? "▲" : "▼";
  return (
    <div
      className="flex md:min-w-[180px] max-md:min-w-0 flex-1 flex-col md:text-[14px] max-md:text-[12px]"
      style={{ gap: 6, lineHeight: 1 }}
    >
      <div className="flex items-center justify-between gap-3 max-md:gap-1">
        <span className="font-light text-white whitespace-nowrap">
          {row.name}
        </span>
        <span className="font-bold text-white whitespace-nowrap">
          {row.price}
        </span>
      </div>
      <div className="flex items-center justify-between gap-3 max-md:gap-1">
        <span className="font-bold whitespace-nowrap" style={{ color }}>
          <span style={{ fontSize: 10 }}>{arrow} </span>
          {row.change}
        </span>
        <span className="font-bold whitespace-nowrap" style={{ color }}>
          {row.absChange}
        </span>
      </div>
    </div>
  );
}

function LogoRow({
  moreOpen,
  onToggleMore,
  accountOpen,
  onOpenAccount,
}: {
  moreOpen: boolean;
  onToggleMore: () => void;
  accountOpen: boolean;
  onOpenAccount: () => void;
}) {
  return (
    <div className="w-full bg-white">
      <div className="mx-auto w-full max-w-[1440px] px-4 pt-4 lg:pt-6 lg:px-[120px]">
        <div className="flex h-16 items-center justify-between">
          {/* Left cluster */}
          <div className="flex items-center" style={{ gap: 20, minWidth: 48 }}>
            <button
              aria-label="選單"
              aria-expanded={moreOpen}
              onClick={onToggleMore}
              className="flex items-center justify-center lg:hidden"
            >
              <Menu
                style={{ width: 24, height: 24, color: "#1d1d1f" }}
                strokeWidth={1.8}
              />
            </button>
            <div className="hidden lg:inline-flex">
              <SearchAction />
            </div>
            <div className="hidden lg:inline-flex">
              <LanguageAction panelAlign="left" />
            </div>
            {/* Subscriber text link, left of the logo — legacy HKEJ_HomePage_Subscriber_Textbox
                (220×30). Explicit position 2; zero width until a creative serves. */}
            <div className="hidden lg:inline-flex" data-nav-textlinks>
              <AdSlot unit="SponsorBanner2" sizes={SUBSCRIBER_TEXTLINK_SIZES} className="inline-flex" />
            </div>
          </div>

          {/* Logo */}
          <Link
            href="/"
            aria-label="信報財經新聞"
            className="relative shrink-0"
            style={{ width: 255, height: 66 }}
          >
            <Image
              src="/images/nav-v2/hkej-logo-v2.png"
              alt="信報財經新聞"
              fill
              priority
              className="object-contain"
              sizes="255px"
            />
          </Link>

          {/* Right cluster */}
          <div
            className="flex items-center justify-end"
            style={{ gap: 20, minWidth: 48 }}
          >
            {/* Subscriber text link, right of the logo — position 1 (the 33 M-impression unit). */}
            <div className="hidden lg:inline-flex" data-nav-textlinks>
              <AdSlot unit="SponsorBanner1" sizes={SUBSCRIBER_TEXTLINK_SIZES} className="inline-flex" />
            </div>
            <Link
              href="/subscription"
              // QA: "懸停時缺少按鈕顏色變化的效果" — the CTA had no hover state.
              // The resting colour is a custom property, NOT an inline
              // `background-color`: an inline declaration outranks every class,
              // so a `hover:bg-*` written here would never apply.
              className="hidden items-center justify-center bg-[color:var(--cta)] font-bold transition-colors duration-150 hover:bg-[#477a68] lg:inline-flex"
              style={{
                ["--cta" as string]: CORE_COLORS.subscribeGreen,
                color: "#ffffff",
                padding: "7px 8px",
                borderRadius: 2,
                fontSize: 14,
                lineHeight: 1,
              }}
            >
              立即訂閱
            </Link>
            <button
              type="button"
              aria-label="會員"
              aria-haspopup="dialog"
              aria-expanded={accountOpen}
              onClick={onOpenAccount}
              className={`${NAV_ICON_BUTTON} rounded-full`}
              style={{ width: 24, height: 24 }}
            >
              <User style={{ width: 24, height: 24 }} strokeWidth={1.8} />
            </button>
          </div>
        </div>

      </div>
    </div>
  );
}

function PrimaryNav({
  moreOpen,
  onToggleMore,
}: {
  moreOpen: boolean;
  onToggleMore: () => void;
}) {
  return (
    <nav className="w-full bg-white max-md:border-b max-md:border-[#333]">
      <div className="mx-auto w-full max-w-[1440px] py-4 lg:px-[120px]">
        <ul
          className="flex items-center md:[justify-content:safe_center] overflow-x-auto no-scrollbar px-4"
          style={{
            fontSize: 14,
            lineHeight: "16.8px",
            gap: 14,
          }}
        >
          {primaryNav.map((item) => (
            <li key={item.label} className="shrink-0">
              <Link
                href={item.href}
                className="whitespace-nowrap text-black hover:text-[#0e8f63]"
              >
                {item.label}
              </Link>
            </li>
          ))}
          <li
            className="hidden shrink-0 font-bold lg:block"
            style={{ color: "#096344" }}
          >
            -
          </li>
          <li className="hidden shrink-0 lg:block">
            <button
              type="button"
              onClick={onToggleMore}
              aria-expanded={moreOpen}
              className="inline-flex items-center font-bold"
              style={{ color: "#0e8f63", gap: 4 }}
            >
              <span>更多</span>
              <ChevronDown
                style={{
                  width: 16,
                  height: 16,
                  transform: moreOpen ? "rotate(180deg)" : undefined,
                  transition: "transform 150ms",
                }}
                strokeWidth={2}
              />
            </button>
          </li>
        </ul>
      </div>
    </nav>
  );
}

function SubNav() {
  const topicsRef = useRef<HTMLDivElement>(null);
  const topicEdges = useScrollEdges(topicsRef);
  return (
    <div className="w-full bg-white">
      <div className="mx-auto w-full max-w-[1440px] lg:px-[120px]">
        {/* Desktop — single horizontally-scrollable row, centered */}
        <div
          className="hidden md:flex items-center justify-center overflow-x-auto no-scrollbar"
          style={{
            borderTop: "1px solid #333",
            borderBottom: "1px solid #333",
            paddingTop: 12,
            paddingBottom: 12,
          }}
        >
          <span
            className="shrink-0 whitespace-nowrap font-bold"
            style={{
              color: "#d70d19",
              fontSize: 12,
              lineHeight: "14px",
              paddingRight: 12,
            }}
          >
            熱門主題
          </span>
          {hotTopics.map((topic, i) => (
            <span key={topic.label} className="flex shrink-0 items-center">
              {/* QA: "熱門主題: add hover effect to the items". The resting
                  colour is a class, not an inline style, so `hover:` can win. */}
              {/* A 熱門主題 chip names a TOPIC, so it opens that topic's tag
                  page. Three of the five pointed at /features — the section
                  index, not the topic — so different chips landed on the same
                  page and none of them on the topic itself.
                  Derived from the label rather than the fixture href, which
                  `categoryRoute` also reads and must keep pointing at sections.
                  QA: "熱門主題: items should redirect to the tag page". */}
              <Link
                href={tagRoute(topic.label)}
                className="whitespace-nowrap text-[#333] transition-colors hover:text-[#108a60]"
                style={{
                  fontSize: 12,
                  lineHeight: "14px",
                }}
              >
                {topic.label}
              </Link>
              {i < hotTopics.length - 1 && (
                <span
                  aria-hidden
                  style={{
                    color: "rgba(51,51,51,0.2)",
                    fontSize: 12,
                    padding: "0 12px",
                  }}
                >
                  |
                </span>
              )}
            </span>
          ))}
        </div>

        {/* Mobile — fixed 熱門主題 title + independently scrollable topic row */}
        <div
          className="md:hidden flex items-center pl-4"
          style={{
            borderBottom: "1px solid #333",
            paddingTop: 12,
            paddingBottom: 12,
          }}
        >
          <span
            className="shrink-0 whitespace-nowrap font-bold pr-[12px] bg-white"
            style={{ color: "#d70d19", fontSize: 12, lineHeight: "14px" }}
          >
            熱門主題
          </span>
          {/* The scrollbar is hidden, so the row carries the shared ‹ / ›
              affordances — one per direction that can actually move. */}
          <div className="relative flex-1 min-w-0">
          <div
            ref={topicsRef}
            className="overflow-x-auto no-scrollbar flex items-center pr-4"
          >
            {hotTopics.map((topic, i) => (
              <span key={topic.label} className="flex shrink-0 items-center">
                <Link
                  href={topic.href}
                  className="whitespace-nowrap text-[#333] transition-colors hover:text-[#108a60]"
                  style={{ fontSize: 12, lineHeight: "14px" }}
                >
                  {topic.label}
                </Link>
                {i < hotTopics.length - 1 && (
                  <span
                    aria-hidden
                    style={{
                      color: "rgba(51,51,51,0.2)",
                      fontSize: 12,
                      padding: "0 12px",
                    }}
                  >
                    |
                  </span>
                )}
              </span>
            ))}
          </div>
            <ScrollEdgeArrows scroller={topicsRef} edges={topicEdges} />
          </div>
        </div>
      </div>
    </div>
  );
}
